import { ApolloError } from '@apollo/client'
import { Button, Cell, Grid, HFlow } from 'bold-ui'
import { useAlert } from 'components/alert'
import { useAcessoLotacaoOrEstagio } from 'components/auth/useAcessoLotacao'
import { useSession } from 'components/auth/useSession'
import { useErrorHandler } from 'components/error'
import {
  CidadaoAtendimentoSelectField,
  DateField,
  Form,
  FormRenderProps,
  LocalAtendimentoSelectField,
  SubmitButton,
  TimeField,
} from 'components/form'
import { RegistroTardioJustificativaSelectField } from 'components/form/field/select/JustificativaRegistroTardioSelectField/RegistroTardioJustificativaSelectField'
import {
  LotacaoAndEstagioSelectField,
  LotacaoAndEstagioSelectFieldModel,
} from 'components/form/field/select/LotacaoAndEstagioSelectField/LotacaoAndEstagioSelectField'
import { CIDADAO_PATH } from 'components/layout/SideMenu/SideMenuItems'
import { useFlags } from 'config/useFlagsContext'
import { FormApi } from 'final-form'
import { useCidadaoQuery, useSalvarRegistroTardioMutation } from 'graphql/hooks.generated'
import { useMemo } from 'react'
import { useHistory, useLocation } from 'react-router'
import Permissions from 'types/Permissions'
import { convertDateAndTimeToInstant } from 'util/date/formatDate'
import { metaPath } from 'util/metaPath'
import { cidadaoAusenteModal } from 'view/atendimentos/list/modal/CidadaoAusenteModal'
import { useSaudeIndigenaAuthorization } from 'view/cidadao/authorization/useSaudeIndigenaAuthorization'
import { removeCidadaoIdParam } from 'view/cidadao/utils-cidadao'
import { REGISTRO_TARDIO_PATH } from 'view/registro-tardio/RegistroTardioRootView'

import { calculator } from './calculator-registroTardio'
import { DayDelimiterTooltip } from './components/DayDelimiterTooltip'
import { convertModelToInput } from './converter'
import { RegistroTardioFormModel } from './model-registroTardio'
import { getMinDate } from './utils-registroTardio'
import { isLocalAtendimentoComJustificativa, validator } from './validator-registroTardio'

const path = metaPath<RegistroTardioFormModel>()

export interface ListaRegistroTardioFormProps {
  serverTime: Date
  cidadaoIdFromURL?: ID
  onSubmit(dataAtendimento: number, cidadaoId: ID): Promise<boolean>
}

export default function ListaRegistroTardioForm(props: ListaRegistroTardioFormProps) {
  const { serverTime, cidadaoIdFromURL, onSubmit } = props

  const alert = useAlert()
  const history = useHistory()
  const location = useLocation()
  const handleRejection = useErrorHandler()
  const {
    data: { acesso, profissional },
  } = useSession()
  const { hasAuthorization, acesso: acessoLotacaoOrEstagio } = useAcessoLotacaoOrEstagio()
  const { SAUDE_INDIGENA_ENABLED } = useFlags()

  const { hasCadastrarEditarAldeadoPermission } = useSaudeIndigenaAuthorization()

  const canAtender = hasAuthorization(Permissions.visualizarListaDeAtendimento.registrarAtendimento)

  // TODO [DeLorean]: Aguardando CNES da parte do Sesai para definir como será validado as opções especificas da saúde indígena (#19881)
  const canRegistroIndigena = SAUDE_INDIGENA_ENABLED

  const [salvarRegistroTardio] = useSalvarRegistroTardioMutation()

  const { data: cidadaoData } = useCidadaoQuery({
    fetchPolicy: 'cache-first',
    variables: { id: cidadaoIdFromURL },
    skip: !cidadaoIdFromURL,
  })
  const minDate = getMinDate(serverTime, canRegistroIndigena)

  const lotacao = useMemo(
    () => (canAtender ? ({ ...acesso, profissional: { ...profissional } } as LotacaoAndEstagioSelectFieldModel) : null),
    [acesso, canAtender, profissional]
  )

  const initialValues: RegistroTardioFormModel = useMemo(
    () => ({
      cidadao: cidadaoIdFromURL && cidadaoData?.cidadao && { ...cidadaoData.cidadao, presenteListaAtendimento: false },
      lotacao,
    }),
    [cidadaoData, cidadaoIdFromURL, lotacao]
  )

  const resetFormFields = (formApi: FormApi, values: Partial<RegistroTardioFormModel>) => {
    formApi.getRegisteredFields().forEach((field) => formApi.resetFieldState(field))
    formApi.reset(values)
  }

  const clearExternalCidadaoSelection = () => {
    if (!cidadaoIdFromURL) return

    history.replace({
      pathname: location.pathname,
      search: removeCidadaoIdParam(location.search) || undefined,
    })
  }

  const handleClear = (formApi: FormApi) => {
    clearExternalCidadaoSelection()
    resetFormFields(formApi, { lotacao })
  }

  const handleCidadaoChange = (cidadao?: RegistroTardioFormModel['cidadao']) => {
    if (!cidadao || cidadao.id !== cidadaoIdFromURL) {
      clearExternalCidadaoSelection()
    }
  }

  const handleSubmit = async (values: RegistroTardioFormModel, formApi: FormApi) => {
    const dataRegistro = convertDateAndTimeToInstant(values.dataAtendimento, values.horaAtendimento)
    const sucesso = await onSubmit(dataRegistro, values?.cidadao?.id)

    if (sucesso) {
      try {
        if (values.cidadao.isCidadaoAusente) {
          return cidadaoAusenteModal({
            hasPermission: hasCadastrarEditarAldeadoPermission,
            onConfirm: () =>
              history.push(
                `${CIDADAO_PATH}/${values.cidadao.id}/edit?callbackUrl=registroTardio/cidadao-ausente&callbackParams=cidadaoId=${values.cidadao.id}`
              ),
          })
        }

        const res = await salvarRegistroTardio({
          variables: { input: convertModelToInput(values) },
        })
        alert('success', `Cidadão foi adicionado com sucesso.`)
        setTimeout(() => resetFormFields(formApi, { lotacao }))
        history.push(REGISTRO_TARDIO_PATH)
        return res
      } catch (err) {
        handleRejection(err as ApolloError)
      }
    }
  }

  const renderForm = (formProps: FormRenderProps<RegistroTardioFormModel>) => {
    const { values } = formProps

    const justificativaDisabled =
      !values.localAtendimento || !isLocalAtendimentoComJustificativa(values.localAtendimento.id)

    return (
      <Grid>
        <Cell size={4}>
          <CidadaoAtendimentoSelectField
            name={path.cidadao}
            label='Cidadão'
            ativo
            obito={false}
            verificarPresenteNaListaAtendimento={false}
            addCidadaoCallbackUrl={REGISTRO_TARDIO_PATH}
            onChange={handleCidadaoChange}
            required
          />
        </Cell>
        {!canAtender && (
          <Cell size={4}>
            <LotacaoAndEstagioSelectField
              name={path.lotacao}
              label='Profissional'
              unidadeSaudeId={acessoLotacaoOrEstagio?.unidadeSaude.id}
              canWriteRegistroTardio
              required
            />
          </Cell>
        )}
        <Cell size={2}>
          <DateField
            name={path.dataAtendimento}
            label='Data do Atendimento'
            required
            minDate={minDate}
            maxDate={serverTime}
            calendarProps={{
              renderDay: (day: Date) => <DayDelimiterTooltip day={day} minDate={minDate} maxDate={serverTime} />,
            }}
          />
        </Cell>
        <Cell size={2}>
          <TimeField name={path.horaAtendimento} label='Hora' required placeholder='hh:mm' />
        </Cell>
        {canAtender && <Cell size={2} />}
        <Cell size={4}>
          <LocalAtendimentoSelectField name={path.localAtendimento} required />
        </Cell>
        <Cell size={4}>
          <RegistroTardioJustificativaSelectField
            name={path.justificativa}
            label='Justificativa'
            required={!justificativaDisabled}
            disabled={justificativaDisabled}
          />
        </Cell>
        <Cell size={12}>
          <HFlow justifyContent='flex-end'>
            <Button size='medium' onClick={() => handleClear(formProps.form)}>
              Limpar campos
            </Button>
            <SubmitButton size='medium' handleSubmit={formProps.handleSubmit} dataTestId='adicionarRegistroTardio'>
              Adicionar
            </SubmitButton>
          </HFlow>
        </Cell>
      </Grid>
    )
  }

  const validate = useMemo(() => validator(minDate, canAtender), [canAtender, minDate])

  return (
    <Form<RegistroTardioFormModel>
      initialValues={initialValues}
      render={renderForm}
      onSubmit={handleSubmit}
      validate={validate}
      decorators={[calculator]}
    />
  )
}
